import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useInHouse } from '../api/restaurant';
import { useLaundryMutation, useLaundryOrders, useLaundryPrices, type LaundryOrder, type LaundryStatus } from '../api/laundry';
import { formatDateTime, formatGhs, parseCedis, type Tone } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';
import { RefundDialog } from './Refund';
import { ServicePayDialog } from './ServicePay';

const STATUS: Record<LaundryStatus, { label: string; tone: Tone }> = {
  RECEIVED: { label: 'Received', tone: 'info' },
  WASHING: { label: 'Washing', tone: 'warn' },
  READY: { label: 'Ready', tone: 'good' },
  DELIVERED: { label: 'Handed back', tone: 'brand' },
  CANCELLED: { label: 'Cancelled', tone: '' },
};

type Tab = 'open' | 'done' | 'prices';

export function Laundry() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('open');
  const [adding, setAdding] = useState(false);
  return (
    <div className="page">
      <PageHead eyebrow="Housekeeping" title="Laundry">
        {can('laundry.work') ? <Button variant="primary" onClick={() => setAdding(true)}>Receive laundry</Button> : null}
      </PageHead>
      <div className="seg" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'open'} onClick={() => setTab('open')}>In the laundry</button>
        <button type="button" aria-pressed={tab === 'done'} onClick={() => setTab('done')}>Finished</button>
        {can('laundry.manage') ? <button type="button" aria-pressed={tab === 'prices'} onClick={() => setTab('prices')}>Prices</button> : null}
      </div>
      {tab === 'prices' ? <Prices /> : <Orders show={tab} />}
      {adding ? <NewOrder onClose={() => setAdding(false)} /> : null}
    </div>
  );
}

/* ------------------------------------------------------------------ orders */
function Orders({ show }: { show: 'open' | 'done' }) {
  const q = useLaundryOrders(show);
  const rows = q.data ?? [];
  if (q.isLoading) return <Loading rows={3} />;
  if (q.error) return <ErrorNote error={q.error} retry={() => void q.refetch()} />;
  if (rows.length === 0) return <div className="card flat"><Empty icon="inbox" title={show === 'open' ? 'No laundry in the house' : 'Nothing finished yet'}>{show === 'open' ? 'Receive a guest’s laundry to start an order.' : 'Handed-back and cancelled orders show here.'}</Empty></div>;
  return (
    <div className="list">
      {rows.map((o) => <OrderCard key={o.id} o={o} />)}
    </div>
  );
}

function OrderCard({ o }: { o: LaundryOrder }) {
  const { can } = useAuth();
  const toast = useToast();
  const report = useReport();
  const [dlg, setDlg] = useState<null | 'pay' | 'refund' | 'cancel'>(null);
  const [reason, setReason] = useState('');
  const act = useLaundryMutation((v: { path: string; body?: Record<string, unknown> }) => api(`/laundry/orders/${o.id}/${v.path}`, { method: 'POST', body: v.body ?? {} }));
  const go = (path: string, done: string, body?: Record<string, unknown>, then?: () => void) => act.mutate({ path, ...(body ? { body } : {}) }, { onError: report, onSuccess: () => { toast.ok(done); then?.(); } });
  const live = o.status !== 'DELIVERED' && o.status !== 'CANCELLED';

  return (
    <div className="item">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="title">{o.customerName}</span>
        <Badge tone={STATUS[o.status].tone}>{STATUS[o.status].label}</Badge>
      </div>
      <div className="row small muted">
        <span className="num">{o.reference}</span>
        <span>· {o.pieces} piece{o.pieces === 1 ? '' : 's'}</span>
        <span className="num">· {formatGhs(o.total)}</span>
        <span>· {o.paid ? `paid (${(o.paidWith ?? '').toLowerCase()})` : 'not paid'}</span>
        {o.refunded ? <span>· refunded</span> : null}
      </div>
      <div className="small">{o.lines.map((l) => `${l.qty} × ${l.itemName}`).join(', ')}</div>
      {o.note ? <div className="hint">“{o.note}”</div> : null}
      {o.cancelReason ? <div className="hint">{o.cancelReason}</div> : null}
      <div className="small muted">Received {formatDateTime(o.createdAt)}</div>
      {can('laundry.work') && live ? (
        <div className="row">
          {o.status === 'RECEIVED' ? <Button variant="primary" loading={act.isPending} onClick={() => go('start', 'Washing started')}>Start washing</Button> : null}
          {o.status === 'WASHING' ? <Button variant="primary" loading={act.isPending} onClick={() => go('ready', 'Marked ready')}>Ready</Button> : null}
          {o.status === 'READY' ? (
            <>
              <Button variant="primary" disabled={!o.paid && o.total > 0} loading={act.isPending} onClick={() => go('deliver', 'Handed back')}>Hand back</Button>
              <Button variant="ghost" loading={act.isPending} onClick={() => go('rewash', 'Back to washing')}>Wash again</Button>
            </>
          ) : null}
          {!o.paid && o.total > 0 && can('service.settle') ? <Button onClick={() => setDlg('pay')}>Take payment · {formatGhs(o.total)}</Button> : null}
          {o.status !== 'READY' && !o.paid ? <Button variant="ghost" onClick={() => setDlg('cancel')}>Cancel</Button> : null}
        </div>
      ) : null}
      {o.paid && !o.refunded && can('refund.issue') && o.total > 0 ? <div className="row"><Button variant="ghost" onClick={() => setDlg('refund')}>Refund</Button></div> : null}
      {dlg === 'pay' ? <ServicePayDialog title={`Pay ${o.reference}`} payPath={`/laundry/orders/${o.id}/pay`} total={o.total} bookingId={o.bookingId} onClose={() => setDlg(null)} /> : null}
      {dlg === 'refund' ? <RefundDialog title={`Refund ${o.reference}`} path={`/laundry/orders/${o.id}/refund`} paid={[{ method: o.paidWith ?? 'CASH', amount: o.total }]} onClose={() => setDlg(null)} /> : null}
      <Modal open={dlg === 'cancel'} onClose={() => setDlg(null)} title={`Cancel ${o.reference}?`}>
        <form className="stack" onSubmit={(e: FormEvent) => { e.preventDefault(); if (reason.trim().length >= 3) go('cancel', 'Cancelled', { reason: reason.trim() }, () => { setDlg(null); setReason(''); }); }}>
          <Field id={`cr-${o.id}`} label="Reason"><input id={`cr-${o.id}`} className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus /></Field>
          <div className="modal-foot">
            <Button type="button" onClick={() => setDlg(null)}>Back</Button>
            <Button variant="danger" type="submit" disabled={reason.trim().length < 3} loading={act.isPending}>Cancel order</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

/* --------------------------------------------------------------- new order */
function NewOrder({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const report = useReport();
  const prices = useLaundryPrices();
  const [guestMode, setGuestMode] = useState(true);
  const [search, setSearch] = useState('');
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [qty, setQty] = useState<Record<string, number>>({});
  const guests = useInHouse(search.trim(), guestMode && !bookingId);
  const make = useLaundryMutation((body: Record<string, unknown>) => api('/laundry/orders', { method: 'POST', body }));
  useEffect(() => setBookingId(null), [guestMode]);

  const lines = Object.entries(qty).filter(([, n]) => n > 0).map(([itemTypeId, n]) => ({ itemTypeId, qty: n }));
  const items = prices.data ?? [];
  const total = lines.reduce((s, l) => s + l.qty * (items.find((i) => i.id === l.itemTypeId)?.price ?? 0), 0);
  const who = guestMode ? !!bookingId : name.trim().length >= 2;
  const valid = who && lines.length > 0;
  const set = (id: string, n: number) => setQty({ ...qty, [id]: Math.max(0, Math.min(200, n)) });

  return (
    <Modal open onClose={onClose} title="Receive laundry">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!valid) return;
          make.mutate({ ...(guestMode ? { bookingId } : { customerName: name.trim(), ...(phone.trim() ? { phone: phone.trim() } : {}) }), ...(note.trim() ? { note: note.trim() } : {}), lines }, { onError: report, onSuccess: () => { toast.ok('Laundry received'); onClose(); } });
        }}
      >
        <div className="seg" role="group" aria-label="Whose laundry">
          <button type="button" aria-pressed={guestMode} onClick={() => setGuestMode(true)}>Hotel guest</button>
          <button type="button" aria-pressed={!guestMode} onClick={() => setGuestMode(false)}>Someone else</button>
        </div>
        {guestMode ? (
          bookingId ? (
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span>{guests.data?.find((g) => g.bookingId === bookingId)?.guest ?? 'Guest chosen'}</span>
              <Button type="button" variant="ghost" onClick={() => setBookingId(null)}>Change</Button>
            </div>
          ) : (
            <div className="stack">
              <input className="input" aria-label="Find a guest in the house" placeholder="Guest name or room" value={search} onChange={(e) => setSearch(e.target.value)} />
              <div className="list">
                {(guests.data ?? []).map((g) => (
                  <button key={g.bookingId} type="button" className="item" style={{ textAlign: 'left' }} onClick={() => setBookingId(g.bookingId)}>
                    <span className="title">{g.guest}</span>
                    <span className="small muted">{g.room ? `Room ${g.room}` : 'No room'} · {g.reference}</span>
                  </button>
                ))}
                {guests.data && guests.data.length === 0 ? <p className="small muted">No guest in the house matches.</p> : null}
              </div>
            </div>
          )
        ) : (
          <div className="grid-2">
            <Field id="ln" label="Name"><input id="ln" className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
            <Field id="lp" label="Phone (optional)"><input id="lp" className="input" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
          </div>
        )}
        <div className="eyebrow">Items</div>
        {prices.isLoading ? <Loading rows={2} /> : items.length === 0 ? <p className="small muted">No prices set yet. A manager adds them under Prices.</p> : null}
        {items.map((i) => (
          <div key={i.id} className="row" style={{ justifyContent: 'space-between' }}>
            <span>{i.name} <span className="small muted num">{formatGhs(i.price)}</span></span>
            <span className="row">
              <Button type="button" aria-label={`One less ${i.name}`} disabled={!qty[i.id]} onClick={() => set(i.id, (qty[i.id] ?? 0) - 1)}>−</Button>
              <strong className="num" style={{ minWidth: 28, textAlign: 'center' }} aria-label={`${i.name} count`}>{qty[i.id] ?? 0}</strong>
              <Button type="button" aria-label={`One more ${i.name}`} onClick={() => set(i.id, (qty[i.id] ?? 0) + 1)}>+</Button>
            </span>
          </div>
        ))}
        <Field id="lnote" label="Note (optional)"><input id="lnote" className="input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. stain on the white shirt" /></Field>
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <span className="muted">Total</span>
          <strong className="num">{formatGhs(total)}</strong>
        </div>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Receive</Button>
        </div>
      </form>
    </Modal>
  );
}

/* ------------------------------------------------------------------ prices */
function Prices() {
  const q = useLaundryPrices(true);
  const toast = useToast();
  const report = useReport();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [edit, setEdit] = useState<Record<string, string>>({});
  const add = useLaundryMutation((body: Record<string, unknown>) => api('/laundry/prices', { method: 'POST', body }));
  const patch = useLaundryMutation((v: { id: string; body: Record<string, unknown> }) => api(`/laundry/prices/${v.id}`, { method: 'PATCH', body: v.body }));
  const p = parseCedis(price);

  return (
    <div className="stack">
      <form className="stack card" onSubmit={(e: FormEvent) => { e.preventDefault(); if (name.trim().length >= 2 && p !== null) add.mutate({ name: name.trim(), price: p }, { onError: report, onSuccess: () => { toast.ok('Added to the price list'); setName(''); setPrice(''); } }); }}>
        <div className="eyebrow">Add an item</div>
        <div className="grid-2">
          <Field id="pn" label="Item"><input id="pn" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Shirt" /></Field>
          <Field id="pp" label="Price per piece (GH₵)"><input id="pp" className="input" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>
        </div>
        <Button variant="primary" type="submit" disabled={name.trim().length < 2 || p === null} loading={add.isPending}>Add item</Button>
      </form>
      {q.isLoading ? <Loading rows={2} /> : q.error ? <ErrorNote error={q.error} retry={() => void q.refetch()} /> : (
        <div className="list">
          {(q.data ?? []).map((i) => {
            const np = parseCedis(edit[i.id] ?? '');
            return (
              <div key={i.id} className="item">
                <div className="row" style={{ justifyContent: 'space-between' }}>
                  <span className="title">{i.name}</span>
                  {!i.active ? <Badge>Hidden</Badge> : null}
                </div>
                <div className="row" style={{ alignItems: 'flex-end' }}>
                  <Field id={`lp-${i.id}`} label="Price (GH₵)"><input id={`lp-${i.id}`} className="input" inputMode="decimal" value={edit[i.id] ?? (i.price / 100).toFixed(2)} onChange={(e) => setEdit({ ...edit, [i.id]: e.target.value })} /></Field>
                  <Button disabled={np === null || np === i.price} onClick={() => patch.mutate({ id: i.id, body: { price: np } }, { onError: report, onSuccess: () => toast.ok('Price updated') })}>Save</Button>
                  <Button variant="ghost" onClick={() => patch.mutate({ id: i.id, body: { active: !i.active } }, { onError: report })}>{i.active ? 'Hide' : 'Show'}</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
